<template>
	<ClientTeleport>
		<Transition name="fade">
			<div v-if="visible" class="fixed z-popup inset-0 grid items-end h-full bg-black/20 backdrop-blur-xs" v-bind="$attrs">
				<div
					class="max-h-screen pt-[0.6rem] grid items-end h-[100dvh] mx-auto w-full sm:container lg:items-start"
					:class="isStickyHeader ? 'pt-[0.45rem]' : 'pt-[0.6rem]'"
				>
					<div
						ref="popup"
						class="flex flex-col w-full"
					>
						<div class="max-lg:hidden" :class="isStickyHeader ? 'lg:ml-[4.25rem] lg:w-[calc(100%-11.5rem)]' : 'lg:ml-[8.1rem] lg:w-[calc(100%-20.5rem)]'">
							<slot name="searchBox" />
						</div>
						<div class="bg-background-primary rem:rounded-t-[20px] mt-2.5 max-h-full min-h-0 shadow-lg lg:rounded-xl relative">
							<div class="flex justify-between max-lg:p-4">
								<slot name="headerLeft" />
								<button
									class="p-2 absolute top-1 right-1 z-20 text-icon-primary hover:bg-button-secondary-surface-hover rounded-full transition ease"
									type="button"
									aria-label="Zavřít"
									@click="close"
								>
									<NuxtIcon name="x" filled class="text-xl md:text-2xl" />
								</button>
							</div>
							<div
								class="shrink-1 grow-0 max-h-full min-h-0 overflow-y-auto overflow-auto overscroll-contain"
							>
								<slot v-bind="slotProps" />
							</div>
						</div>
					</div>
				</div>
			</div>
		</Transition>
	</ClientTeleport>
</template>

<script lang="ts" setup>
	import {useScrollLock, onClickOutside} from '@vueuse/core';
	import {useFocusTrap} from '@vueuse/integrations/useFocusTrap';
	import {storeToRefs} from 'pinia';
	import {useSelectboxStore} from '~/stores/selectbox';
	import {useSearchStore} from '~/stores/search';

	type Props = {
		visible: boolean;
		isStickyHeader?: boolean;
	};

	type Emits = {
		(e: 'update:visible', payload: boolean): void;
	};

	const props = defineProps<Props>();
	const emit = defineEmits<Emits>();

	const body = ref<HTMLElement | null>(null);
	const popup = ref<HTMLElement | null>(null);
	const keydownListener = ref();
	const slotProps = computed(() => ({close}));
	const searchStore = useSearchStore();

	const isLocked = useScrollLock(body);
	const {activate: activateFocusTrap, deactivate: deactivateFocusTrap} = useFocusTrap(popup);
	const {selectboxDropdown} = storeToRefs(useSelectboxStore());

	onClickOutside(popup, () => emit('update:visible', false), {ignore: [selectboxDropdown, '[data-shopping-list-picker-panel]', '[data-shopping-list-toggle]']});

	onMounted(() => {
		document.addEventListener('keydown', keydownListener.value);
		body.value = document.body;
	});

	onBeforeUnmount(() => {
		document.removeEventListener('keydown', keydownListener.value);
		deactivateFocusTrap();
	});

	watch(() => props.visible, async (newValue) => {
		isLocked.value = newValue;
		await nextTick();
		newValue ? activateFocusTrap() : deactivateFocusTrap();
	}, {immediate: true});

	keydownListener.value = function (e: KeyboardEvent) {
		if (e.key === 'Escape') {
			emit('update:visible', false);
		}
	};

	function close() {
		emit('update:visible', false);
		searchStore.setVisibleSearchPopup(false);
	}
</script>
